/**
 * AdminPanel - 管理员用户管理面板
 * 只有管理员角色可以访问
 */

import React, { useState, useEffect } from 'react';
import { X, Users, UserCheck, UserX, Key, Trash2, AlertTriangle, RefreshCw, Settings, Sparkle, LogIn, Clock, Shield, Pencil, Tag } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';
import UserAvatar from './UserAvatar';
import CreditLogsModal from './CreditLogsModal';
import { IS_EDU } from '../config/edition'; // 🎓 教育版改口叫「发放」+ 算力券入口

// 🎓 教育版改口叫「发放」，构建时死代码消除
const getChargeWord = () => {
  if (process.env.REACT_APP_SITE_EDITION === 'edu' || (process.env.REACT_APP_SITE_EDITION || '').endsWith('-edu')) {
    return '发放';
  }
  return '充值';
};

export default function AdminPanel({ open, onClose }) {
  const { user: currentUser, hasPermission, isSuperAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [userNotes, setUserNotes] = useState({});
  const [notesDirty, setNotesDirty] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // 重置密码弹窗
  const [resetUser, setResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  
  // 删除确认
  const [deleteUser, setDeleteUser] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // 💰 积分弹窗
  const [chargeUser, setChargeUser] = useState(null);
  const [chargeAmount, setChargeAmount] = useState('');
  const [chargeLoading, setChargeLoading] = useState(false);
  const [chargeOperation, setChargeOperation] = useState('add'); // 'add' | 'deduct'
  const [chargeReason, setChargeReason] = useState('');
  
  // 🎁 默认积分设置
  const [defaultCredits, setDefaultCredits] = useState(0);
  const [defaultCreditsInput, setDefaultCreditsInput] = useState('');
  const [defaultCreditsLoading, setDefaultCreditsLoading] = useState(false);
  
  // 📊 查看积分明细
  const [viewLogsUser, setViewLogsUser] = useState(null);

  // ⏰ 续期弹窗
  const [renewUser, setRenewUser] = useState(null);
  const [renewDays, setRenewDays] = useState('30');
  const [renewAction, setRenewAction] = useState('extend'); // 'extend' | 'set' | 'clear'
  const [renewLoading, setRenewLoading] = useState(false);

  // ✏️ 改名弹窗
  const [renameUser, setRenameUser] = useState(null);
  const [renameUsername, setRenameUsername] = useState('');
  const [renameLoading, setRenameLoading] = useState(false);

  // 🔑 权限编辑弹窗
  const [permUser, setPermUser] = useState(null);
  const [permValues, setPermValues] = useState(null); // null=全部 []=无 [...]= 指定
  const [permLoading, setPermLoading] = useState(false);

  // 🏷️ 称号弹窗
  const [titleUser, setTitleUser] = useState(null);
  const [titleValue, setTitleValue] = useState('');
  const [titleLoading, setTitleLoading] = useState(false);

  // 🏷️ 有效期徽章全局开关（仅超级管理员可见/操作）
  const [showExpiryBadge, setShowExpiryBadge] = useState(false);
  const [expiryBadgeLoading, setExpiryBadgeLoading] = useState(false);

  // 🎖️ 会员功能配置弹窗（仅超级管理员）：等级 → 开放功能
  const [memberCfgOpen, setMemberCfgOpen] = useState(false);
  const [memberCfg, setMemberCfg] = useState(null);        // { education:[], commercial:[...], vip:[...] }
  const [memberLevels, setMemberLevels] = useState([]);    // [{value,label}]
  const [memberFeatures, setMemberFeatures] = useState([]); // [{key,label}]
  const [memberCfgLoading, setMemberCfgLoading] = useState(false);

  const openMemberConfig = async () => {
    setMemberCfgOpen(true);
    setMemberCfgLoading(true);
    try {
      const data = await api.get('/api/admin/membership-config');
      setMemberLevels(data.levels || []);
      setMemberFeatures(data.features || []);
      setMemberCfg(data.config || {});
    } catch (err) {
      alert(err.message || '获取会员功能配置失败');
      setMemberCfgOpen(false);
    } finally {
      setMemberCfgLoading(false);
    }
  };

  const toggleMemberFeature = (level, featureKey) => {
    setMemberCfg(prev => {
      const cur = Array.isArray(prev?.[level]) ? prev[level] : [];
      const next = cur.includes(featureKey) ? cur.filter(f => f !== featureKey) : [...cur, featureKey];
      return { ...prev, [level]: next };
    });
  };

  const handleSaveMemberConfig = async () => {
    setMemberCfgLoading(true);
    try {
      const result = await api.put('/api/admin/membership-config', { config: memberCfg });
      if (result.error) throw new Error(result.error);
      setMemberCfgOpen(false);
    } catch (err) {
      alert(err.message || '保存会员功能配置失败');
    } finally {
      setMemberCfgLoading(false);
    }
  };

  // 加载有效期徽章开关状态
  useEffect(() => {
    if (open && currentUser?.role === 'super_admin') {
      api.get('/api/settings/show-expiry-badge').then(data => {
        setShowExpiryBadge(!!data.enabled);
      }).catch(() => {});
    }
  }, [open, currentUser?.role]);

  const toggleExpiryBadge = async () => {
    setExpiryBadgeLoading(true);
    try {
      const data = await api.put('/api/settings/show-expiry-badge', { enabled: !showExpiryBadge });
      if (data.success) setShowExpiryBadge(data.enabled);
    } catch (err) {
      console.error('切换有效期显示失败:', err);
    } finally {
      setExpiryBadgeLoading(false);
    }
  };

  // 加载用户列表
  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get('/api/admin/users');
      setUsers(data);
      setUserNotes(
        Object.fromEntries((data || []).map(u => [u.id, u.note || '']))
      );
      setNotesDirty(false);
    } catch (err) {
      console.error('加载用户失败:', err);
      setError(err.message || '加载用户列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleNoteChange = (userId, value) => {
    const clipped = Array.from(value).slice(0, 12).join('');
    setUserNotes(prev => ({ ...prev, [userId]: clipped }));
    setNotesDirty(true);
  };

  const handleSaveNotes = async () => {
    const payload = users
      .map(u => {
        const draft = typeof userNotes[u.id] === 'string' ? userNotes[u.id] : (u.note || '');
        return { id: u.id, note: draft.trim() };
      })
      .filter(item => {
        const original = (users.find(u => u.id === item.id)?.note || '').trim();
        return item.note !== original;
      });

    if (payload.length === 0) {
      setNotesDirty(false);
      return;
    }

    setSavingNotes(true);
    try {
      const result = await api.put('/api/admin/users/notes', { notes: payload });
      if (result?.success) {
        setUsers(prev => prev.map(u => {
          const changed = payload.find(p => p.id === u.id);
          return changed ? { ...u, note: changed.note } : u;
        }));
        setNotesDirty(false);
        alert('备注已保存');
      }
    } catch (err) {
      alert(err.message || '保存备注失败');
    } finally {
      setSavingNotes(false);
    }
  };
  
  // 🎁 加载默认积分设置
  const loadDefaultCredits = async () => {
    try {
      const data = await api.get('/api/admin/settings/default-credits');
      setDefaultCredits(data.defaultCredits || 0);
      setDefaultCreditsInput(String(data.defaultCredits || 0));
    } catch (err) {
      console.error('加载默认积分失败:', err);
    }
  };
  
  // 🎁 保存默认积分设置
  const handleSaveDefaultCredits = async () => {
    const amount = parseInt(defaultCreditsInput);
    if (isNaN(amount) || amount < 0) {
      alert('请输入有效的积分数量');
      return;
    }
    
    setDefaultCreditsLoading(true);
    try {
      const result = await api.put('/api/admin/settings/default-credits', {
        defaultCredits: amount
      });
      if (result.success) {
        setDefaultCredits(result.defaultCredits);
        alert('默认积分设置已保存');
      }
    } catch (err) {
      alert(err.message || '保存失败');
    } finally {
      setDefaultCreditsLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadUsers();
      loadDefaultCredits();
    }
  }, [open]);

  // 切换用户启用/禁用状态
  const handleToggleUser = async (user) => {
    try {
      const result = await api.put(`/api/admin/users/${user.id}/toggle`);
      if (result.success) {
        setUsers(prev => prev.map(u => 
          u.id === user.id ? { ...u, enabled: result.enabled } : u
        ));
      }
    } catch (err) {
      alert(err.message || '操作失败');
    }
  };
  
  // 💰 积分操作
  const handleChargeCredits = async () => {
    if (!chargeUser || !chargeAmount) return;
    const amount = parseInt(chargeAmount);
    if (isNaN(amount) || amount <= 0) {
      alert('请输入有效的积分数量');
      return;
    }
    
    setChargeLoading(true);
    try {
      const result = await api.put(`/api/admin/users/${chargeUser.id}/credits`, {
        amount: chargeOperation === 'deduct' ? -amount : amount,
        operation: 'add',
        reason: chargeReason.trim() || `管理员${chargeOperation === 'deduct' ? '扣除' : getChargeWord()}积分`
      });
      if (result.success) {
        setUsers(prev => prev.map(u => {
          if (u.id === chargeUser.id) return { ...u, credits: result.credits };
          // 非超管转账后，操作者自身余额也变化，同步刷新列表里的自己
          if (typeof result.operatorCredits === 'number' && u.id === currentUser?.id) {
            return { ...u, credits: result.operatorCredits };
          }
          return u;
        }));
        setChargeUser(null);
        setChargeAmount('');
        setChargeReason('');
        setChargeOperation('add');
        const opNote = typeof result.operatorCredits === 'number'
          ? `，你的余额: ${result.operatorCredits}`
          : '';
        alert(`${chargeOperation === 'deduct' ? '扣除' : getChargeWord()}成功！对方当前积分: ${result.credits}${opNote}`);
      }
    } catch (err) {
      alert(err.message || `${chargeOperation === 'deduct' ? '扣除' : getChargeWord()}失败`);
    } finally {
      setChargeLoading(false);
    }
  };

  // 重置密码
  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      alert('密码长度至少6位');
      return;
    }
    
    setResetLoading(true);
    try {
      await api.post(`/api/admin/users/${resetUser.id}/reset-password`, {
        newPassword
      });
      alert(`用户 ${resetUser.username} 的密码已重置`);
      setResetUser(null);
      setNewPassword('');
    } catch (err) {
      alert(err.message || '重置密码失败');
    } finally {
      setResetLoading(false);
    }
  };

  // 删除用户
  const handleDeleteUser = async () => {
    setDeleteLoading(true);
    try {
      await api.delete(`/api/admin/users/${deleteUser.id}`);
      setUsers(prev => prev.filter(u => u.id !== deleteUser.id));
      setDeleteUser(null);
    } catch (err) {
      alert(err.message || '删除用户失败');
    } finally {
      setDeleteLoading(false);
    }
  };

  // 🆕 管理员登录到指定用户（在新窗口打开）
  const handleLoginAsUser = async (user) => {
    if (!window.confirm(`确认要在新窗口中登录到用户 "${user.username}" 的账号吗？`)) {
      return;
    }
    
    try {
      const result = await api.post(`/api/admin/users/${user.id}/login-as`);
      console.log('[AdminPanel] login-as API 响应:', result);
      
      if (result.success && result.tempToken) {
        // 打开新窗口，URL 带临时 token（注意：首页是 / 不是 /dashboard）
        const url = `/?temp_token=${result.tempToken}`;
        console.log('[AdminPanel] 打开新窗口 URL:', url);
        
        // 使用 noopener 打开新窗口（返回值会是 null，这是正常的）
        window.open(url, '_blank', 'noopener');
      } else {
        console.error('[AdminPanel] API 响应缺少 tempToken:', result);
        alert('获取临时登录凭证失败');
      }
    } catch (err) {
      console.error('[AdminPanel] login-as 错误:', err);
      alert(err.message || '登录失败');
    }
  };

  // ✏️ 改名 handler
  const handleRenameUser = async () => {
    if (!renameUser || !renameUsername.trim()) return;
    setRenameLoading(true);
    try {
      const result = await api.put(`/api/admin/users/${renameUser.id}/username`, { username: renameUsername.trim() });
      if (result.success) {
        setUsers(prev => prev.map(u => u.id === renameUser.id ? { ...u, username: result.username } : u));
        setRenameUser(null);
      }
    } catch (err) {
      alert(err.message || '改名失败');
    } finally {
      setRenameLoading(false);
    }
  };

  // 🔑 打开权限编辑（先拉取当前权限）
  const openPermissions = async (user) => {
    setPermLoading(true);
    setPermUser(user);
    try {
      const data = await api.get(`/api/admin/users/${user.id}/permissions`);
      setPermValues(data.permissions); // null or []
    } catch (err) {
      alert(err.message || '获取权限失败');
      setPermUser(null);
    } finally {
      setPermLoading(false);
    }
  };

  const handleSavePermissions = async () => {
    if (!permUser) return;
    setPermLoading(true);
    try {
      await api.put(`/api/admin/users/${permUser.id}/permissions`, { permissions: permValues });
      setPermUser(null);
    } catch (err) {
      alert(err.message || '保存权限失败');
    } finally {
      setPermLoading(false);
    }
  };

  // 🏷️ 保存称号
  const handleSaveTitle = async () => {
    if (!titleUser) return;
    setTitleLoading(true);
    try {
      const result = await api.put(`/api/admin/users/${titleUser.id}/admin-title`, { title: titleValue.trim() });
      if (result.success) {
        setUsers(prev => prev.map(u => u.id === titleUser.id ? { ...u, admin_title: result.admin_title } : u));
        setTitleUser(null);
      }
    } catch (err) {
      alert(err.message || '设置称号失败');
    } finally {
      setTitleLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.8)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
      <div style={{
        width: 1400, maxWidth: '95vw', maxHeight: '80vh', background: '#1a1a1d',
        borderRadius: 16, overflow: 'hidden',
        border: '1px solid rgba(255,255,255,0.1)',
        display: 'flex', flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '20px 24px', borderBottom: '1px solid rgba(255,255,255,0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Users size={24} style={{ color: '#3b82f6' }} />
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#fff' }}>
              用户管理
            </h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* 🎖️ 会员功能配置（仅超级管理员） */}
            {currentUser?.role === 'super_admin' && (
              <button
                onClick={openMemberConfig}
                style={{
                  background: 'rgba(167,139,250,0.15)',
                  border: '1px solid rgba(167,139,250,0.4)',
                  padding: '5px 10px', borderRadius: 8, cursor: 'pointer',
                  color: '#a78bfa',
                  display: 'flex', alignItems: 'center', gap: 5, fontSize: 12,
                  transition: 'all 0.2s', whiteSpace: 'nowrap'
                }}
                title="配置每个会员等级开放哪些特殊功能"
              >
                <Shield size={14} />
                <span>会员功能配置</span>
              </button>
            )}
            {/* 🏷️ 有效期徽章全局开关（仅超级管理员可见） */}
            {currentUser?.role === 'super_admin' && (
              <button
                onClick={toggleExpiryBadge}
                disabled={expiryBadgeLoading}
                style={{
                  background: showExpiryBadge ? 'rgba(34,197,94,0.15)' : 'rgba(255,255,255,0.05)',
                  border: `1px solid ${showExpiryBadge ? 'rgba(34,197,94,0.4)' : 'rgba(255,255,255,0.1)'}`,
                  padding: '5px 10px', borderRadius: 8, cursor: 'pointer',
                  color: showExpiryBadge ? '#22c55e' : '#94a3b8',
                  display: 'flex', alignItems: 'center', gap: 5, fontSize: 12,
                  transition: 'all 0.2s', whiteSpace: 'nowrap'
                }}
                title={showExpiryBadge ? '点击关闭：用户画布不显示有效期' : '点击开启：用户画布显示剩余天数'}
              >
                <Clock size={14} />
                <span>{showExpiryBadge ? '有效期：显示' : '有效期：隐藏'}</span>
              </button>
            )}
            <button
              onClick={loadUsers}
              disabled={loading}
              style={{
                background: 'rgba(255,255,255,0.05)', border: 'none',
                padding: '8px', borderRadius: 8, cursor: 'pointer',
                color: '#94a3b8', display: 'flex', alignItems: 'center'
              }}
              title="刷新"
            >
              <RefreshCw size={18} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
            </button>
            <button
              onClick={onClose}
              style={{
                background: 'transparent', border: 'none',
                padding: '8px', cursor: 'pointer', color: '#666'
              }}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '16px 24px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>
              加载中...
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#ef4444' }}>
              {error}
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  <th style={{ padding: '12px 8px', textAlign: 'left', color: '#94a3b8', fontWeight: 500, fontSize: 13, whiteSpace: 'nowrap' }}>ID</th>
                  <th style={{ padding: '12px 8px', textAlign: 'left', color: '#94a3b8', fontWeight: 500, fontSize: 13, whiteSpace: 'nowrap' }}>头像</th>
                  <th style={{ padding: '12px 8px', textAlign: 'left', color: '#94a3b8', fontWeight: 500, fontSize: 13, whiteSpace: 'nowrap' }}>用户名</th>
                  <th style={{ padding: '12px 8px', textAlign: 'left', color: '#94a3b8', fontWeight: 500, fontSize: 13, whiteSpace: 'nowrap' }}>备注</th>
                  <th style={{ padding: '12px 8px', textAlign: 'left', color: '#94a3b8', fontWeight: 500, fontSize: 13, whiteSpace: 'nowrap' }}>积分</th>
                  <th style={{ padding: '12px 8px', textAlign: 'left', color: '#94a3b8', fontWeight: 500, fontSize: 13, whiteSpace: 'nowrap' }}>角色</th>
                  <th style={{ padding: '12px 8px', textAlign: 'left', color: '#94a3b8', fontWeight: 500, fontSize: 13, whiteSpace: 'nowrap' }}>会员等级</th>
                  <th style={{ padding: '12px 8px', textAlign: 'left', color: '#94a3b8', fontWeight: 500, fontSize: 13, whiteSpace: 'nowrap' }}>状态</th>
                  <th style={{ padding: '12px 8px', textAlign: 'left', color: '#94a3b8', fontWeight: 500, fontSize: 13, whiteSpace: 'nowrap' }}>有效期</th>
                  <th style={{ padding: '12px 8px', textAlign: 'left', color: '#94a3b8', fontWeight: 500, fontSize: 13, whiteSpace: 'nowrap' }}>手机号</th>
                  <th style={{ padding: '12px 8px', textAlign: 'left', color: '#94a3b8', fontWeight: 500, fontSize: 13, whiteSpace: 'nowrap' }}>创建时间</th>
                  <th style={{ padding: '12px 8px', textAlign: 'right', color: '#94a3b8', fontWeight: 500, fontSize: 13, whiteSpace: 'nowrap' }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <td style={{ padding: '14px 8px', color: '#666', fontSize: 13 }}>
                      {user.id}
                    </td>
                    <td style={{ padding: '14px 8px' }}>
                      <UserAvatar 
                        avatar={user.avatar}
                        username={user.username}
                        size={32}
                        fontSize={14}
                        border="2px solid rgba(255,255,255,0.1)"
                      />
                    </td>
                    <td style={{ padding: '14px 8px', color: '#fff', fontSize: 14, fontWeight: 500, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={user.username}>
                      {user.username}
                    </td>
                    <td style={{ padding: '14px 8px' }}>
                      <input
                        type="text"
                        value={userNotes[user.id] ?? ''}
                        onChange={e => handleNoteChange(user.id, e.target.value)}
                        maxLength={12}
                        placeholder="备注(12字内)"
                        style={{
                          width: 120,
                          padding: '6px 8px',
                          borderRadius: 6,
                          border: '1px solid rgba(255,255,255,0.12)',
                          background: 'rgba(255,255,255,0.04)',
                          color: '#e2e8f0',
                          fontSize: 12,
                          outline: 'none'
                        }}
                      />
                    </td>
                    <td style={{ padding: '14px 8px', whiteSpace: 'nowrap', minWidth: 90 }}>
                      {/* 所有角色（含超管）均显示真实积分并可管理，2026-06-16 取消超管 ∞ 豁免 */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span
                            onClick={() => setViewLogsUser(user)}
                            style={{
                              color: '#fbbf24', fontSize: 13, fontWeight: 500,
                              display: 'flex', alignItems: 'center', gap: 4,
                              cursor: 'pointer',
                              padding: '2px 6px',
                              borderRadius: 4,
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'rgba(251,191,36,0.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'transparent';
                            }}
                            title="查看积分明细"
                          >
                            <Sparkle size={13} color="#fbbf24" strokeWidth={2.5} /> {Math.round(user.credits || 0)}
                          </span>
                          <button
                            onClick={() => {
                              setChargeUser(user);
                              setChargeAmount('');
                              setChargeReason('');
                              setChargeOperation('add');
                            }}
                            style={{
                              background: 'rgba(251,191,36,0.15)', border: 'none',
                              padding: '2px 6px', borderRadius: 4, cursor: 'pointer',
                              color: '#fbbf24', fontSize: 11
                            }}
                            title="管理积分"
                          >
                            ±
                          </button>
                        </div>
                    </td>
                    <td style={{ padding: '14px 8px' }}>
                      {(() => {
                        const canToggle = (currentUser?.role === 'super_admin' || currentUser?.role === 'admin') && user.role !== 'super_admin' && user.id !== currentUser.id;
                        const newRole = user.role === 'admin' ? 'user' : 'admin';
                        const tooltipText = user.role === 'admin' ? '点击降级为普通用户' : '点击提升为管理员';
                        const roleLabel = user.role === 'super_admin' ? '系统管理员' : user.role === 'admin' ? (user.admin_title || '管理员') : '普通用户';
                        const bgColor = user.role === 'super_admin' ? 'rgba(245,158,11,0.15)' : user.role === 'admin' ? 'rgba(59,130,246,0.15)' : 'rgba(255,255,255,0.05)';
                        const fgColor = user.role === 'super_admin' ? '#f59e0b' : user.role === 'admin' ? '#3b82f6' : '#94a3b8';

                        return (
                          <span
                            title={canToggle ? tooltipText : ''}
                            onClick={canToggle ? async () => {
                              const label = newRole === 'admin' ? '提升为管理员' : '降级为普通用户';
                              if (!window.confirm(`确认要将用户 "${user.username}" ${label}吗？`)) return;
                              try {
                                const result = await api.put(`/api/admin/users/${user.id}/role`, { role: newRole });
                                if (result.error) {
                                  alert(result.error);
                                  return;
                                }
                                setUsers(prev => prev.map(u => u.id === user.id ? { ...u, role: newRole } : u));
                              } catch (err) {
                                console.error('角色变更失败:', err);
                                alert(err.message || '操作失败');
                              }
                            } : undefined}
                            style={{
                              fontSize: 11, padding: '3px 8px', borderRadius: 4,
                              background: bgColor,
                              color: fgColor,
                              cursor: canToggle ? 'pointer' : 'default',
                              transition: 'all 0.2s',
                            }}
                            onMouseEnter={canToggle ? (e) => {
                              e.currentTarget.style.outline = '1px solid ' + fgColor;
                              e.currentTarget.style.outlineOffset = '1px';
                            } : undefined}
                            onMouseLeave={canToggle ? (e) => {
                              e.currentTarget.style.outline = 'none';
                            } : undefined}
                          >
                            {roleLabel}
                          </span>
                        );
                      })()}
                    </td>
                    <td style={{ padding: '14px 8px' }}>
                      {/* 🎖️ 会员等级下拉（所有管理员可改） */}
                      <select
                        value={user.membership_level || 'education'}
                        onChange={async (e) => {
                          const level = e.target.value;
                          const prev = user.membership_level || 'education';
                          setUsers(list => list.map(u => u.id === user.id ? { ...u, membership_level: level } : u));
                          try {
                            const result = await api.put(`/api/admin/users/${user.id}/membership`, { level });
                            if (result.error) throw new Error(result.error);
                          } catch (err) {
                            alert(err.message || '修改会员等级失败');
                            setUsers(list => list.map(u => u.id === user.id ? { ...u, membership_level: prev } : u));
                          }
                        }}
                        style={{
                          fontSize: 12, padding: '4px 8px', borderRadius: 6,
                          background: 'rgba(255,255,255,0.04)',
                          border: '1px solid rgba(255,255,255,0.12)',
                          color: '#e2e8f0', outline: 'none', cursor: 'pointer',
                        }}
                      >
                        <option value="education">教育版</option>
                        <option value="commercial">商用版</option>
                        <option value="vip">VIP版</option>
                      </select>
                    </td>
                    <td style={{ padding: '14px 8px' }}>
                      <span style={{
                        fontSize: 11, padding: '3px 8px', borderRadius: 4,
                        background: user.enabled ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                        color: user.enabled ? '#22c55e' : '#ef4444'
                      }}>
                        {user.enabled ? '正常' : '已禁用'}
                      </span>
                    </td>
                    <td style={{ padding: '14px 8px' }}>
                      {(() => {
                        const openRenew = () => { setRenewUser(user); setRenewDays('30'); setRenewAction('extend'); };
                        if (!user.expires_at) {
                          return (
                            <span
                              onClick={openRenew}
                              style={{ color: '#71717a', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}
                              title="点击管理有效期"
                            >
                              <Clock size={12} /> 永久
                            </span>
                          );
                        }
                        const now = new Date();
                        const exp = new Date(user.expires_at);
                        const diffMs = exp - now;
                        const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
                        let color, label;
                        if (diffDays <= 0) {
                          color = '#71717a'; label = '已过期';
                        } else if (diffDays <= 7) {
                          color = '#ef4444'; label = `${diffDays}天`;
                        } else if (diffDays <= 30) {
                          color = '#f59e0b'; label = `${diffDays}天`;
                        } else {
                          color = '#22c55e'; label = `${diffDays}天`;
                        }
                        return (
                          <span
                            onClick={openRenew}
                            style={{ color, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}
                            title="点击管理有效期"
                          >
                            <Clock size={12} /> {label}
                          </span>
                        );
                      })()}
                    </td>
                    <td style={{ padding: '14px 8px', color: '#94a3b8', fontSize: 12, whiteSpace: 'nowrap' }}>
                      {user.phone || '-'}
                    </td>
                    <td style={{ padding: '14px 8px', color: '#666', fontSize: 12 }}>
                      {user.created_at ? new Date(user.created_at).toLocaleString('zh-CN', {
                        year: 'numeric', month: '2-digit', day: '2-digit',
                        hour: '2-digit', minute: '2-digit'
                      }) : '-'}
                    </td>
                    <td style={{ padding: '14px 8px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                        {/* 启用/禁用（系统管理员不可被禁用） */}
                        {user.role !== 'super_admin' && (
                        <button
                          onClick={() => handleToggleUser(user)}
                          style={{
                            background: user.enabled ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)',
                            border: 'none', padding: '6px 8px', borderRadius: 6,
                            cursor: 'pointer', color: user.enabled ? '#f87171' : '#4ade80',
                            display: 'flex', alignItems: 'center', gap: 4, fontSize: 12
                          }}
                          title={user.enabled ? '禁用用户' : '启用用户'}
                        >
                          {user.enabled ? <UserX size={14} /> : <UserCheck size={14} />}
                        </button>
                        )}
                        
                        {/* 重置密码（系统管理员不可被他人管理） */}
                        {user.role !== 'super_admin' && (
                        <button
                          onClick={() => setResetUser(user)}
                          style={{
                            background: 'rgba(255,255,255,0.05)',
                            border: 'none', padding: '6px 8px', borderRadius: 6,
                            cursor: 'pointer', color: '#94a3b8',
                            display: 'flex', alignItems: 'center', gap: 4, fontSize: 12
                          }}
                          title="重置密码"
                        >
                          <Key size={14} />
                        </button>
                        )}
                        
                        {/* 🆕 登录（系统管理员不可被他人登录） */}
                        {user.role !== 'super_admin' && (
                        <button
                          onClick={() => handleLoginAsUser(user)}
                          style={{
                            background: 'rgba(59,130,246,0.1)',
                            border: 'none', padding: '6px 8px', borderRadius: 6,
                            cursor: 'pointer', color: '#60a5fa',
                            display: 'flex', alignItems: 'center', gap: 4, fontSize: 12
                          }}
                          title="登录到该用户"
                        >
                          <LogIn size={14} />
                        </button>
                        )}

                        {/* ✏️ 改名（需 user_rename 权限；不能改系统管理员，普通管理员不能改其他管理员） */}
                        {hasPermission('user_rename') && user.role !== 'super_admin' && !(currentUser?.role === 'admin' && user.role === 'admin') && (
                          <button
                            onClick={() => { setRenameUser(user); setRenameUsername(user.username); }}
                            style={{
                              background: 'rgba(168,85,247,0.1)',
                              border: 'none', padding: '6px 8px', borderRadius: 6,
                              cursor: 'pointer', color: '#a855f7',
                              display: 'flex', alignItems: 'center', gap: 4, fontSize: 12
                            }}
                            title="改用户名（登录名）"
                          >
                            <Pencil size={14} />
                          </button>
                        )}

                        {/* 🏷️ 称号（系统管理员对admin用户） */}
                        {currentUser?.role === 'super_admin' && user.role === 'admin' && (
                          <button
                            onClick={() => { setTitleUser(user); setTitleValue(user.admin_title || ''); }}
                            style={{
                              background: 'rgba(59,130,246,0.1)',
                              border: 'none', padding: '6px 8px', borderRadius: 6,
                              cursor: 'pointer', color: '#3b82f6',
                              display: 'flex', alignItems: 'center', gap: 4, fontSize: 12
                            }}
                            title="设置管理员称号"
                          >
                            <Tag size={14} />
                          </button>
                        )}

                        {/* 🔑 权限（系统管理员对admin用户） */}
                        {currentUser?.role === 'super_admin' && user.role === 'admin' && (
                          <button
                            onClick={() => openPermissions(user)}
                            style={{
                              background: 'rgba(245,158,11,0.1)',
                              border: 'none', padding: '6px 8px', borderRadius: 6,
                              cursor: 'pointer', color: '#f59e0b',
                              display: 'flex', alignItems: 'center', gap: 4, fontSize: 12
                            }}
                            title="权限设置"
                          >
                            <Shield size={14} />
                          </button>
                        )}
                        
                        {/* 删除（系统管理员不可被删除） */}
                        {user.role !== 'super_admin' && (
                        <button
                          onClick={() => setDeleteUser(user)}
                          style={{
                            background: 'rgba(239,68,68,0.1)',
                            border: 'none', padding: '6px 8px', borderRadius: 6,
                            cursor: 'pointer', color: '#f87171',
                            display: 'flex', alignItems: 'center', gap: 4, fontSize: 12
                          }}
                          title="删除用户"
                        >
                          <Trash2 size={14} />
                        </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          
          {/* �🎁 默认积分设置区域 */}
          <div style={{
            marginTop: 24, padding: 16, background: 'rgba(251,191,36,0.05)',
            borderRadius: 12, border: '1px solid rgba(251,191,36,0.2)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <Settings size={16} style={{ color: '#fbbf24' }} />
              <span style={{ color: '#fbbf24', fontSize: 14, fontWeight: 500 }}>
                新用户默认积分
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ color: '#94a3b8', fontSize: 13 }}>
                新注册用户将获得
              </span>
              <input
                type="number"
                min="0"
                value={defaultCreditsInput}
                onChange={e => setDefaultCreditsInput(e.target.value)}
                style={{
                  width: 100, padding: '8px 12px', borderRadius: 6,
                  background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.1)',
                  color: '#fff', fontSize: 14, outline: 'none', textAlign: 'center'
                }}
              />
              <span style={{ color: '#94a3b8', fontSize: 13 }}>积分</span>
              <button
                onClick={handleSaveDefaultCredits}
                disabled={defaultCreditsLoading || defaultCreditsInput === String(defaultCredits)}
                style={{
                  background: '#fbbf24', border: 'none',
                  padding: '8px 16px', borderRadius: 6, cursor: 'pointer',
                  color: '#000', fontSize: 13, fontWeight: 500,
                  opacity: (defaultCreditsLoading || defaultCreditsInput === String(defaultCredits)) ? 0.5 : 1,
                  marginLeft: 'auto'
                }}
              >
                {defaultCreditsLoading ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px', borderTop: '1px solid rgba(255,255,255,0.1)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <span style={{ color: '#666', fontSize: 12 }}>
            共 {users.length} 个用户
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              onClick={handleSaveNotes}
              disabled={savingNotes || !notesDirty}
              style={{
                background: '#3b82f6',
                border: 'none',
                padding: '10px 20px',
                borderRadius: 8,
                cursor: (savingNotes || !notesDirty) ? 'not-allowed' : 'pointer',
                color: '#fff',
                fontSize: 14,
                opacity: (savingNotes || !notesDirty) ? 0.5 : 1
              }}
            >
              {savingNotes ? '保存中...' : '保存'}
            </button>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.05)', border: 'none',
              padding: '10px 20px', borderRadius: 8, cursor: 'pointer',
              color: '#fff', fontSize: 14
            }}
          >
            关闭
          </button>
          </div>
        </div>
      </div>

      {/* 重置密码弹窗 */}
      {resetUser && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 1100,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            width: 360, background: '#1a1a1d', borderRadius: 12,
            border: '1px solid rgba(255,255,255,0.1)', padding: 24
          }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16, color: '#fff' }}>
              重置密码 - {resetUser.username}
            </h3>
            <input
              type="password"
              placeholder="输入新密码（至少6位）"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px', borderRadius: 8,
                background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.1)',
                color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box'
              }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setResetUser(null); setNewPassword(''); }}
                style={{
                  background: 'rgba(255,255,255,0.05)', border: 'none',
                  padding: '10px 16px', borderRadius: 8, cursor: 'pointer',
                  color: '#94a3b8', fontSize: 14
                }}
              >
                取消
              </button>
              <button
                onClick={handleResetPassword}
                disabled={resetLoading}
                style={{
                  background: '#3b82f6', border: 'none',
                  padding: '10px 16px', borderRadius: 8, cursor: 'pointer',
                  color: '#fff', fontSize: 14, opacity: resetLoading ? 0.5 : 1
                }}
              >
                {resetLoading ? '处理中...' : '确认重置'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 删除确认弹窗 */}
      {deleteUser && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 1100,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            width: 360, background: '#1a1a1d', borderRadius: 12,
            border: '1px solid rgba(255,255,255,0.1)', padding: 24
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <AlertTriangle size={24} style={{ color: '#ef4444' }} />
              <h3 style={{ margin: 0, fontSize: 16, color: '#fff' }}>
                删除用户
              </h3>
            </div>
            <p style={{ margin: '0 0 8px', color: '#e2e8f0', fontSize: 14 }}>
              确定要删除用户 <strong>{deleteUser.username}</strong> 吗？
            </p>
            <p style={{ margin: 0, color: '#f87171', fontSize: 12 }}>
              ⚠️ 此操作将删除该用户的所有项目和资源，且无法撤销！
            </p>
            <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setDeleteUser(null)}
                style={{
                  background: 'rgba(255,255,255,0.05)', border: 'none',
                  padding: '10px 16px', borderRadius: 8, cursor: 'pointer',
                  color: '#94a3b8', fontSize: 14
                }}
              >
                取消
              </button>
              <button
                onClick={handleDeleteUser}
                disabled={deleteLoading}
                style={{
                  background: '#ef4444', border: 'none',
                  padding: '10px 16px', borderRadius: 8, cursor: 'pointer',
                  color: '#fff', fontSize: 14, opacity: deleteLoading ? 0.5 : 1
                }}
              >
                {deleteLoading ? '删除中...' : '确认删除'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 💰 积分弹窗 */}
      {chargeUser && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 1100,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            width: 360, background: '#1a1a1d', borderRadius: 12,
            border: '1px solid rgba(255,255,255,0.1)', padding: 24
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <Sparkle size={24} color="#fbbf24" strokeWidth={2.5} />
              <h3 style={{ margin: 0, fontSize: 16, color: '#fff' }}>
                积分管理
              </h3>
            </div>
            
            {/* 操作类型切换 */}
            <div style={{ 
              display: 'flex', 
              gap: 8, 
              marginBottom: 16,
              padding: 4,
              background: 'rgba(255,255,255,0.03)',
              borderRadius: 8
            }}>
              <button
                onClick={() => setChargeOperation('add')}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  background: chargeOperation === 'add' ? 'rgba(34,197,94,0.15)' : 'transparent',
                  border: chargeOperation === 'add' ? '1px solid rgba(34,197,94,0.3)' : '1px solid transparent',
                  borderRadius: 6,
                  color: chargeOperation === 'add' ? '#22c55e' : '#94a3b8',
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                ➕ {getChargeWord()}
              </button>
              <button
                onClick={() => setChargeOperation('deduct')}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  background: chargeOperation === 'deduct' ? 'rgba(239,68,68,0.15)' : 'transparent',
                  border: chargeOperation === 'deduct' ? '1px solid rgba(239,68,68,0.3)' : '1px solid transparent',
                  borderRadius: 6,
                  color: chargeOperation === 'deduct' ? '#ef4444' : '#94a3b8',
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                ➖ 扣除
              </button>
            </div>
            
            <p style={{ margin: '0 0 16px', color: '#94a3b8', fontSize: 13 }}>
              用户: <strong style={{ color: '#fff' }}>{chargeUser.username}</strong>
            </p>
            <p style={{ margin: '0 0 12px', color: '#666', fontSize: 12 }}>
              当前余额: <span style={{ color: '#fbbf24', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Sparkle size={12} color="#fbbf24" strokeWidth={2.5} /> {Math.round(chargeUser.credits || 0)}</span>
            </p>
            {!isSuperAdmin && (
              <p style={{ margin: '0 0 12px', padding: '8px 10px', background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.2)', borderRadius: 6, color: '#fbbf24', fontSize: 12, lineHeight: 1.5 }}>
                {chargeOperation === 'deduct'
                  ? '扣除对方积分将退回到你自己的余额。'
                  : `${getChargeWord()}将从你的余额扣除，你当前余额: ${(users.find(u => u.id === currentUser?.id)?.credits ?? 0)}。余额不足将无法${getChargeWord()}。`}
              </p>
            )}
            
            {/* 金额输入 */}
            <input
              type="number"
              placeholder={`输入${chargeOperation === 'deduct' ? '扣除' : getChargeWord()}积分数量`}
              value={chargeAmount}
              onChange={(e) => setChargeAmount(e.target.value)}
              min="1"
              style={{
                width: '100%', padding: '12px',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 8, color: '#fff', fontSize: 14,
                outline: 'none', boxSizing: 'border-box',
                marginBottom: 12
              }}
            />
            
            {/* 原因输入 */}
            <textarea
              placeholder="输入原因（可选）"
              value={chargeReason}
              onChange={(e) => setChargeReason(e.target.value)}
              rows={3}
              style={{
                width: '100%', padding: '12px',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 8, color: '#fff', fontSize: 13,
                outline: 'none', boxSizing: 'border-box',
                resize: 'none',
                fontFamily: 'inherit'
              }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
              <button
                onClick={() => { 
                  setChargeUser(null); 
                  setChargeAmount(''); 
                  setChargeReason('');
                  setChargeOperation('add');
                }}
                style={{
                  background: 'rgba(255,255,255,0.05)', border: 'none',
                  padding: '10px 16px', borderRadius: 8, cursor: 'pointer',
                  color: '#94a3b8', fontSize: 14
                }}
              >
                取消
              </button>
              <button
                onClick={handleChargeCredits}
                disabled={chargeLoading || !chargeAmount}
                style={{
                  background: chargeOperation === 'deduct' ? '#ef4444' : '#22c55e',
                  border: 'none',
                  padding: '10px 16px', borderRadius: 8, cursor: 'pointer',
                  color: '#fff', fontSize: 14, fontWeight: 500,
                  opacity: (chargeLoading || !chargeAmount) ? 0.5 : 1
                }}
              >
                {chargeLoading ? '处理中...' : `确认${chargeOperation === 'deduct' ? '扣除' : getChargeWord()}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ⏰ 续期弹窗 */}
      {renewUser && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', zIndex: 1100,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            width: 360, background: '#1a1a1d', borderRadius: 16,
            border: '1px solid rgba(255,255,255,0.15)', padding: 24
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, color: '#fff', fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Clock size={18} color="#8b5cf6" /> 管理有效期
              </h3>
              <button onClick={() => setRenewUser(null)} style={{ background: 'transparent', border: 'none', color: '#666', cursor: 'pointer' }}>
                <X size={18} />
              </button>
            </div>
            <p style={{ color: '#94a3b8', fontSize: 13, margin: '0 0 16px' }}>
              用户：<strong style={{ color: '#fff' }}>{renewUser.username}</strong>
              {renewUser.expires_at ? (
                <span style={{ marginLeft: 8, fontSize: 12 }}>
                  到期：{new Date(renewUser.expires_at).toLocaleDateString('zh-CN')}
                </span>
              ) : ' (当前永久)'}
            </p>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              {[
                { key: 'extend', label: '续期' },
                { key: 'set', label: '重设' },
                { key: 'clear', label: '永久' },
              ].map(o => (
                <button key={o.key} onClick={() => setRenewAction(o.key)} style={{
                  flex: 1, padding: '8px 0', borderRadius: 8, fontSize: 13, cursor: 'pointer',
                  background: renewAction === o.key ? 'rgba(139,92,246,0.2)' : 'rgba(255,255,255,0.05)',
                  border: `1px solid ${renewAction === o.key ? 'rgba(139,92,246,0.5)' : 'rgba(255,255,255,0.1)'}`,
                  color: renewAction === o.key ? '#a78bfa' : '#94a3b8'
                }}>
                  {o.label}
                </button>
              ))}
            </div>
            {renewAction !== 'clear' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <input
                  type="number" min="1" value={renewDays}
                  onChange={e => setRenewDays(e.target.value)}
                  style={{
                    flex: 1, padding: '10px 12px', borderRadius: 8,
                    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)',
                    color: '#fff', fontSize: 14, outline: 'none'
                  }}
                />
                <span style={{ color: '#94a3b8', fontSize: 13, whiteSpace: 'nowrap' }}>天</span>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setRenewUser(null)} style={{
                flex: 1, padding: '10px', borderRadius: 8, fontSize: 14,
                background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                color: '#94a3b8', cursor: 'pointer'
              }}>取消</button>
              <button
                disabled={renewLoading}
                onClick={async () => {
                  setRenewLoading(true);
                  try {
                    const body = { action: renewAction };
                    if (renewAction !== 'clear') body.days = renewDays;
                    const result = await api.put(`/api/admin/users/${renewUser.id}/expires`, body);
                    if (result.success) {
                      setUsers(prev => prev.map(u => u.id === renewUser.id ? { ...u, expires_at: result.expires_at } : u));
                      setRenewUser(null);
                    }
                  } catch (err) {
                    alert(err.message || '操作失败');
                  } finally {
                    setRenewLoading(false);
                  }
                }}
                style={{
                  flex: 1, padding: '10px', borderRadius: 8, fontSize: 14, fontWeight: 500,
                  background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                  border: 'none', color: '#fff', cursor: renewLoading ? 'not-allowed' : 'pointer',
                  opacity: renewLoading ? 0.6 : 1
                }}
              >{renewLoading ? '处理中...' : '确认'}</button>
            </div>
          </div>
        </div>
      )}

      {/* 📊 积分明细弹窗 */}
      <CreditLogsModal
        open={!!viewLogsUser}
        onClose={() => setViewLogsUser(null)}
        userId={viewLogsUser?.id}
        userName={viewLogsUser?.username}
      />

      {/* ✏️ 改名弹窗 */}
      {renameUser && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: 360, background: '#1a1a1d', borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)', padding: 24 }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 16, color: '#fff' }}>改用户名 - {renameUser.username}</h3>
            <p style={{ margin: '0 0 16px', fontSize: 12, color: '#f59e0b' }}>⚠️ 这是登录名。改完后该用户需用新用户名登录。</p>
            <input
              type="text"
              value={renameUsername}
              onChange={e => setRenameUsername(e.target.value)}
              placeholder="输入新用户名（2-30字符）"
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
              <button onClick={() => setRenameUser(null)} style={{ background: 'rgba(255,255,255,0.05)', border: 'none', padding: '10px 16px', borderRadius: 8, cursor: 'pointer', color: '#94a3b8', fontSize: 14 }}>取消</button>
              <button onClick={handleRenameUser} disabled={renameLoading || !renameUsername.trim()} style={{ background: '#a855f7', border: 'none', padding: '10px 16px', borderRadius: 8, cursor: 'pointer', color: '#fff', fontSize: 14, opacity: (renameLoading || !renameUsername.trim()) ? 0.5 : 1 }}>
                {renameLoading ? '处理中...' : '确认改名'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🏷️ 称号弹窗 */}
      {titleUser && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: 360, background: '#1a1a1d', borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)', padding: 24 }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 16, color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Tag size={16} color="#3b82f6" /> 管理员称号 - {titleUser.username}
            </h3>
            <p style={{ margin: '0 0 16px', fontSize: 12, color: '#64748b' }}>显示在角色列的自定义名字（如"运营管理员"）。不影响登录名和权限，留空则恢复默认"管理员"。</p>
            <input
              type="text"
              value={titleValue}
              onChange={e => setTitleValue(e.target.value)}
              maxLength={16}
              placeholder="例如：运营管理员（最多16字）"
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
              <button onClick={() => setTitleUser(null)} style={{ background: 'rgba(255,255,255,0.05)', border: 'none', padding: '10px 16px', borderRadius: 8, cursor: 'pointer', color: '#94a3b8', fontSize: 14 }}>取消</button>
              <button onClick={handleSaveTitle} disabled={titleLoading} style={{ background: '#3b82f6', border: 'none', padding: '10px 16px', borderRadius: 8, cursor: 'pointer', color: '#fff', fontSize: 14, opacity: titleLoading ? 0.5 : 1 }}>
                {titleLoading ? '保存中...' : '保存称号'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🔑 权限编辑弹窗 */}
      {permUser && (() => {
        const ALL_PERMS = [
          { key: 'user_view', label: '查看用户列表' },
          { key: 'user_create', label: '创建用户' },
          { key: 'user_edit', label: '编辑用户（备注/有效期/密码）' },
          { key: 'user_rename', label: '修改用户名（登录名）' },
          { key: 'user_delete', label: '删除用户' },
          { key: 'user_toggle', label: '启用/禁用用户' },
          { key: 'user_login_as', label: '登录到用户账号' },
          { key: 'user_role', label: '提升普通用户为管理员' },
          { key: 'credits_manage', label: `积分${getChargeWord()}/扣除` },
          { key: 'credits_settings', label: '设置默认积分' },
          { key: 'invite_codes', label: '邀请码管理' },
          // 🎓 算力券只有教育版有（商业版走支付），常量折叠后这一项在商业版产物里消失
          ...(IS_EDU ? [{ key: 'vouchers', label: '算力券管理' }] : []),
          { key: 'distribution', label: '放号管理' },
          { key: 'ai_providers', label: 'AI模型配置' },
          { key: 'system_settings', label: '系统配置（存储/短信/语音）' },
          { key: 'login_branding', label: '登录界面管理' },
          { key: 'fonts', label: '字体管理' },
          { key: 'expiry_badge', label: '有效期徽章开关' },
        ];
        const isAll = permValues === null;
        const toggle = (key) => {
          const cur = permValues === null ? ALL_PERMS.map(p => p.key) : (permValues || []);
          const next = cur.includes(key) ? cur.filter(k => k !== key) : [...cur, key];
          setPermValues(next);
        };
        return (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: 440, maxHeight: '80vh', background: '#1a1a1d', borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)', padding: 24, display: 'flex', flexDirection: 'column' }}>
              <h3 style={{ margin: '0 0 4px', fontSize: 16, color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Shield size={16} color="#f59e0b" /> 权限设置 - {permUser.username}
              </h3>
              <p style={{ margin: '0 0 16px', fontSize: 12, color: '#64748b' }}>勾选该管理员可使用的功能。不勾选任何权限则账号无法操作任何管理功能。</p>
              {/* 全部 checkbox */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, cursor: 'pointer', color: '#f59e0b', fontSize: 13, fontWeight: 500 }}>
                <input type="checkbox" checked={isAll} onChange={() => setPermValues(isAll ? [] : null)} style={{ width: 14, height: 14, cursor: 'pointer' }} />
                全部权限（系统管理员默认）
              </label>
              <div style={{ flex: 1, overflowY: 'auto', borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px' }}>
                {ALL_PERMS.map(({ key, label }) => {
                  const checked = isAll || (Array.isArray(permValues) && permValues.includes(key));
                  return (
                    <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', color: checked ? '#e2e8f0' : '#64748b', fontSize: 12 }}>
                      <input type="checkbox" checked={checked} onChange={() => toggle(key)} style={{ width: 13, height: 13, cursor: 'pointer', accentColor: '#f59e0b' }} />
                      {label}
                    </label>
                  );
                })}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
                <button onClick={() => setPermUser(null)} style={{ background: 'rgba(255,255,255,0.05)', border: 'none', padding: '10px 16px', borderRadius: 8, cursor: 'pointer', color: '#94a3b8', fontSize: 14 }}>取消</button>
                <button onClick={handleSavePermissions} disabled={permLoading} style={{ background: '#f59e0b', border: 'none', padding: '10px 16px', borderRadius: 8, cursor: 'pointer', color: '#000', fontSize: 14, fontWeight: 500, opacity: permLoading ? 0.5 : 1 }}>
                  {permLoading ? '保存中...' : '保存权限'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* 🎖️ 会员功能配置弹窗（仅超级管理员）：勾选每个等级开放的特殊功能 */}
      {memberCfgOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: 560, maxWidth: '92vw', background: '#1a1a1d', borderRadius: 16, border: '1px solid rgba(255,255,255,0.1)', padding: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Shield size={16} color="#a78bfa" /> 会员功能配置
              </h3>
              <button onClick={() => setMemberCfgOpen(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#666' }}>
                <X size={20} />
              </button>
            </div>
            <p style={{ margin: '0 0 16px', fontSize: 12, color: '#94a3b8', lineHeight: 1.6 }}>
              勾选每个会员等级可使用的特殊功能。修改用户的会员等级即可让其获得/失去对应功能。
            </p>
            {memberCfgLoading && !memberCfg ? (
              <div style={{ textAlign: 'center', padding: 30, color: '#666' }}>加载中...</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {memberLevels.map(lv => (
                  <div key={lv.value} style={{ border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '12px 14px' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', marginBottom: 10 }}>{lv.label}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                      {memberFeatures.map(f => {
                        const checked = Array.isArray(memberCfg?.[lv.value]) && memberCfg[lv.value].includes(f.key);
                        return (
                          <label key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: checked ? '#fff' : '#94a3b8', cursor: 'pointer' }}>
                            <input type="checkbox" checked={checked} onChange={() => toggleMemberFeature(lv.value, f.key)} style={{ cursor: 'pointer' }} />
                            {f.label}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
              <button onClick={() => setMemberCfgOpen(false)} style={{ background: 'rgba(255,255,255,0.05)', border: 'none', padding: '10px 16px', borderRadius: 8, cursor: 'pointer', color: '#94a3b8', fontSize: 14 }}>取消</button>
              <button onClick={handleSaveMemberConfig} disabled={memberCfgLoading} style={{ background: '#a78bfa', border: 'none', padding: '10px 16px', borderRadius: 8, cursor: 'pointer', color: '#000', fontSize: 14, fontWeight: 500, opacity: memberCfgLoading ? 0.5 : 1 }}>
                {memberCfgLoading ? '保存中...' : '保存配置'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
